import { useCallback, useEffect, useState } from "react";

import Confetti from "react-confetti";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, IconButton } from "@similarweb/ui-components";

import { TrackingAction } from "app/components/PackageSwitcher/consts/TrackingActions";
import type { ISwitchPackageTrackingInformation } from "app/components/PackageSwitcher/types/switchPackageTracking.types";
import { SwitchPackageStageName } from "app/custom-hooks/switchPackage/useSubmitPackageChange.types";
import { useWindowSize } from "app/custom-hooks/useWIndowSize";
import { allTrackers } from "app/services/track/track";

import type { ISwitchPackageStageData } from "./stageConfig";
import {
    ButtonCloseWrapper,
    ButtonsHeaderWrapper,
    Container,
    Description,
    IconContainer,
    Title,
    Actions,
    ConfettiWrapper,
} from "./SwitchPackageStage.styled";

interface ISwitchPackageStage {
    switchPackageStageData: ISwitchPackageStageData;
    onClose: () => void;
    onActionSuccess: () => void;
    trackingInformation?: ISwitchPackageTrackingInformation;
}

export const SwitchPackageStage = ({
    switchPackageStageData,
    onClose,
    onActionSuccess,
    trackingInformation,
}: ISwitchPackageStage) => {
    const { translate } = useTranslate();
    const { isCloseButtonAvailable, icon, title, showActionButtons, description } = switchPackageStageData;
    const { width, height } = useWindowSize();
    const windowWidth = width ?? 0;
    const windowHeight = height ?? 0;
    const [isLoading, setIsLoading] = useState(false);

    const trackState = useCallback(
        (action: string, subSubName: string) => {
            allTrackers.dispatchTrackEvent(
                "one click package",
                action,
                `${trackingInformation?.hookTrackingKey}+${trackingInformation?.targetProductName}/${subSubName}`,
            );
        },
        [trackingInformation?.hookTrackingKey, trackingInformation?.targetProductName],
    );

    const handleCloseClick = useCallback(
        (isActionSuccess: boolean = false): void => {
            if (isActionSuccess) {
                trackState(TrackingAction.CLICK, "purchase successful done");
                onActionSuccess();
                setIsLoading(true);
                return;
            }
            onClose();
        },
        [onActionSuccess, onClose, trackState],
    );

    useEffect(() => {
        if (switchPackageStageData.stage === SwitchPackageStageName.SUCCESS) {
            trackState(TrackingAction.SHOW, "purchase successful");
        }
    }, [switchPackageStageData.stage, trackState, trackingInformation]);

    useEffect(() => {
        if (switchPackageStageData.stage === SwitchPackageStageName.FAILURE) {
            trackState(TrackingAction.SHOW, "purchase error");
        }
    }, [switchPackageStageData.stage, trackState, trackingInformation]);

    useEffect(() => {
        if (switchPackageStageData.stage === SwitchPackageStageName.PAYMENT_FAILURE) {
            trackState(TrackingAction.SHOW, "purchase payment failure");
        }
    }, [switchPackageStageData.stage, trackState, trackingInformation]);

    return (
        <>
            <ButtonsHeaderWrapper>
                {isCloseButtonAvailable ? (
                    <ButtonCloseWrapper>
                        <IconButton
                            type="flat"
                            iconName="close"
                            onClick={() => handleCloseClick()}
                            iconSize="xs"
                            data-automation="package-switcher-stage-close-btn"
                        />
                    </ButtonCloseWrapper>
                ) : null}
            </ButtonsHeaderWrapper>
            <Container $isSuccess={switchPackageStageData.stage === SwitchPackageStageName.SUCCESS}>
                <IconContainer>{icon}</IconContainer>
                {title ? (
                    <Title
                        fontVariant="h4"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translate(title)}
                    </Title>
                ) : null}
                {description ? <Description>{description}</Description> : null}
                {showActionButtons ? (
                    <Actions>
                        <Button
                            type="primary"
                            onClick={() => handleCloseClick(true)}
                            isLoading={isLoading}
                            data-automation="package-switcher-stage-done-btn"
                        >
                            {translate("global.nt.cancel.popup.done")}
                        </Button>
                    </Actions>
                ) : null}
            </Container>
            {switchPackageStageData.stage === SwitchPackageStageName.SUCCESS ? (
                <ConfettiWrapper>
                    <Confetti
                        width={windowWidth}
                        height={windowHeight}
                        numberOfPieces={200}
                        recycle={false}
                    />
                </ConfettiWrapper>
            ) : null}
        </>
    );
};
